import {getCookie} from "helpers/common";

const profileService = {
    _url: `${process.env.REACT_APP_USER_AUTH_URL}/auth`,

    /**
     * Get current user profile
     * GET /profile/me
     */
    async getProfile() {
        const token = getCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE);
        
        try {
            const res = await fetch(`${this._url}/profile/me`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (res.status === 401) {
                throw new Error("UNAUTHORIZED");
            }

            if (res.status === 404) {
                throw new Error("User not found");
            }

            if (res.status >= 200 && res.status < 300) {
                const data = await res.json();
                return data;
            }

            const errorData = await res.json();
            throw new Error(errorData.message || "Failed to fetch profile");
        } catch (error) {
            console.error("Error fetching profile:", error);
            throw error;
        }
    },

    /**
     * Update profile information
     * PUT /profile/update
     * @param {Object} payload - { first_name, last_name, email, phone, address }
     */
    async updateProfile(payload) {
        const token = getCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE);

        try {
            const res = await fetch(`${this._url}/profile/update`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (res.status === 401) {
                throw new Error("UNAUTHORIZED");
            }

            if (res.status === 404) {
                throw new Error("User not found");
            }

            if (res.status === 409) {
                throw new Error("Email already exists");
            }

            if (res.status === 400) {
                const errorData = await res.json();
                throw new Error(errorData.message || "Validation error");
            }

            if (res.status >= 200 && res.status < 300) {
                const data = await res.json();
                return data;
            }

            const errorData = await res.json();
            throw new Error(errorData.message || "Failed to update profile");
        } catch (error) {
            console.error("Error updating profile:", error);
            throw error;
        }
    },

    /**
     * Upload/Update profile picture
     * PUT /profile/picture
     * @param {File} file - Image file
     */
    async uploadProfilePicture(file) {
        const token = getCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE);

        try {
            const formData = new FormData();
            formData.append("profilePicture", file);

            const res = await fetch(`${this._url}/profile/picture`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            if (res.status === 401) {
                throw new Error("UNAUTHORIZED");
            }

            if (res.status === 400) {
                const errorData = await res.json();
                throw new Error(errorData.message || "Invalid file format");
            }

            if (res.status >= 200 && res.status < 300) {
                const data = await res.json();
                return data;
            }

            const errorData = await res.json();
            throw new Error(errorData.message || "Failed to upload profile picture");
        } catch (error) {
            console.error("Error uploading profile picture:", error);
            throw error;
        }
    },

    /**
     * Remove profile picture
     * DELETE /profile/picture
     */
    async removeProfilePicture() {
        const token = getCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE);

        try {
            const res = await fetch(`${this._url}/profile/picture`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (res.status === 401) {
                throw new Error("UNAUTHORIZED");
            }

            if (res.status >= 200 && res.status < 300) {
                const data = await res.json();
                return data;
            }

            const errorData = await res.json();
            throw new Error(errorData.message || "Failed to remove profile picture");
        } catch (error) {
            console.error("Error removing profile picture:", error);
            throw error;
        }
    },

    /**
     * Change user password
     * PUT /profile/change-password
     * @param {Object} payload - { currentPassword, newPassword, confirmPassword }
     */
    async changePassword(payload) {
        const token = getCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE);

        try {
            const res = await fetch(`${this._url}/profile/change-password`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (res.status === 401) {
                const errorData = await res.json();
                throw new Error(errorData.message || "Current password is incorrect");
            }

            if (res.status === 404) {
                throw new Error("User not found");
            }

            if (res.status === 400) {
                const errorData = await res.json();
                throw new Error(errorData.message || "Validation error");
            }

            if (res.status >= 200 && res.status < 300) {
                const data = await res.json();
                return data;
            }

            const errorData = await res.json();
            throw new Error(errorData.message || "Failed to change password");
        } catch (error) {
            console.error("Error changing password:", error);
            throw error;
        }
    },
};

export default profileService;
